import { Box, Typography, Button, Switch, styled } from "@mui/material";
import HelpIcon from '@mui/icons-material/Help';
import { CustomTooltip } from "src/content/keywordResearch/CustomTooltip";
import { newCardsCss } from "src/content/dashboard/DashboardCss";
import { keywordResearchDashBoardCss } from "src/content/keywordResearch/keywordResearchCSS";
import { KeywordOverviewResultCardsCss } from "src/content/keywordResearch/keywordOverview/keywordOverviewCSS";
import { useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "src/redux/store";
import ReviewAutomationDialog from "./ReviewAutomationDialog";
import { defaultPayloadSettings } from "./SettingsForm";
import { useSaveReviewAutomationSettings } from "../hooks";
import { ReviewAutomationSettingsResponse } from "../types";

const CustomSwitch = styled(Switch)(() => {
  let activeColor = 'var(--color-secondary)';

  return {
    width: 48,
    height: 26,
    padding: 0,
    display: 'flex',
    '&.Mui-disabled': {
      cursor: 'not-allowed',
      pointerEvents: 'auto',
    },
    '& .MuiSwitch-switchBase': {
      padding: 0,
      margin: 3,
      transitionDuration: '300ms',
      '&.Mui-disabled': {
        cursor: 'not-allowed',
        pointerEvents: 'auto',
        '& .MuiSwitch-input': {
          cursor: 'not-allowed',
          pointerEvents: 'auto',
        }
      },
      '&.Mui-checked': {
        transform: 'translateX(22px)',
        color: '#fff',
        '& + .MuiSwitch-track': {
          backgroundColor: activeColor,
          opacity: 1,
          border: 0,
        },
        '&.Mui-disabled + .MuiSwitch-track': {
          opacity: 0.5,
        },
      },
    },
    '& .MuiSwitch-thumb': {
      boxSizing: 'border-box',
      width: 20,
      height: 20,
      boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.2)',
      backgroundColor: 'white',
      '&.Mui-disabled': {
        cursor: 'not-allowed',
        pointerEvents: 'auto',
      },
    },
    '& .MuiSwitch-track': {
      borderRadius: 13,
      backgroundColor: 'var(--color-bg-mid-grey)',
      opacity: 1,
      transition: 'background-color 300ms',
      '&.Mui-disabled': {
        cursor: 'not-allowed',
        pointerEvents: 'auto',
      },
    },
  };
});

interface AutomationSettingProps {
  accountId?: string;
  locationId?: string;
  settingsData?: ReviewAutomationSettingsResponse;
  isSettingsLoading?: boolean;
  isBlocked?: boolean;
  isMapped?: boolean;
}

function AutomationSetting({
  accountId = "",
  locationId = "",
  settingsData,
  isSettingsLoading = false,
  isBlocked = false,
  isMapped = true,
}: AutomationSettingProps) {
  const [openSettings, setOpenSettings] = useState(false);

  const { _id: reduxSiteId } = useSelector(
    (state: RootState) => state?.siteAudit?.siteAuditDetails
  ) ?? {};

  const { saveSettings, isLoading: isSaving } = useSaveReviewAutomationSettings();

  const isNotMapped = !accountId || !locationId || !isMapped;
  const isActionDisabled = isBlocked || isNotMapped || isSettingsLoading || isSaving;

  const isEnabled = !isBlocked && !isNotMapped && (settingsData?.settings?.enabled ?? false);

  const handleToggleEnable = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isBlocked || isNotMapped) {
      return;
    }
    const newEnabled = event.target.checked;
    const current = settingsData?.settings;

    const payload = newEnabled && current
      ? {
          ai_instructions: current.ai_instructions || defaultPayloadSettings.ai_instructions,
          tone: current.tone || defaultPayloadSettings.tone,
          banned_phrases: current.banned_phrases || defaultPayloadSettings.banned_phrases,
          personalize_with_name: current.personalize_with_name ?? defaultPayloadSettings.personalize_with_name,
          skip_5_star: current.skip_5_star ?? defaultPayloadSettings.skip_5_star,
          skip_short_reviews: current.skip_short_reviews ?? defaultPayloadSettings.skip_short_reviews,
          reply_only_with_text: current.reply_only_with_text ?? defaultPayloadSettings.reply_only_with_text,
          require_approval_for_1_2_star: current.require_approval_for_1_2_star ?? defaultPayloadSettings.require_approval_for_1_2_star,
        }
      : defaultPayloadSettings;

    saveSettings({
      site_id: reduxSiteId,
      accountId,
      locationId,
      enabled: newEnabled,
      ...payload,
    });
  };

  const handleOpen = () => {
    setOpenSettings(true);
  };

  const handleClose = () => {
    setOpenSettings(false);
  };

  return (
    <div className="w-full px-6 py-4">
      <div className="flex items-center justify-between gap-2">
        <Box className="flex items-center gap-3">
          <Typography sx={newCardsCss?.headTextCss}>
            Automation Settings
          </Typography>
          <CustomTooltip
            title={
              <Box>
                <Typography
                  sx={keywordResearchDashBoardCss?.customTollTipText}
                >
                  AI-generated overview highlighting your website's primary content gaps, SEO strengths, and action plan.
                </Typography>
              </Box>
            }
            placement="bottom"
            arrow
          >
            <HelpIcon sx={KeywordOverviewResultCardsCss?.helpIcon} />
          </CustomTooltip>
        </Box>

        <div className="flex justify-center items-center gap-4">
          <div className="flex justify-center items-center gap-2">
            <p className="font-semibold text-sm">Enable Automation</p>
            <CustomSwitch
              checked={isEnabled}
              disabled={isActionDisabled}
              onChange={handleToggleEnable}
            />
          </div>
          {/* Save Button */}
          <Button
            variant="contained"
            onClick={handleOpen}
            disabled={isActionDisabled}
            sx={{
              minWidth: 150,
              height: 42,
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            Show Settings
          </Button>
        </div>
      </div>

      <ReviewAutomationDialog
        open={openSettings}
        onClose={handleClose}
        accountId={accountId}
        locationId={locationId}
        settingsData={settingsData}
      />
    </div>
  );
}

export default AutomationSetting;
